import { type StoreApi } from 'zustand';
import { useEffect } from 'react';

import { useBreakpoint } from './useBreakpoint';
import { shouldPersistToCookie } from '../utils/mapVisibilityUtils';
import { type MapVisibilityState } from '../providers/MapVisibilityStoreProvider';

/**
 * Hook that handles persisting map visibility state to a cookie.
 * Subscribes to store changes and updates the cookie when the state changes.
 */
export const useMapCookiePersistence = (
  mapVisibilityStore: StoreApi<MapVisibilityState>,
  setIsMapToggledOnCookie: (value: string) => void,
) => {
  const { breakpoint } = useBreakpoint();

  useEffect(() => {
    if (!shouldPersistToCookie(breakpoint)) {
      return;
    }

    const updateCookie = (state: MapVisibilityState) => {
      if (!state.hasMounted) return;

      setIsMapToggledOnCookie(state.isMapActive ? 'true' : 'false');
    };

    updateCookie(mapVisibilityStore.getState());

    // Subscribe to future changes and update the cookie after initialization.
    const unsubscribe = mapVisibilityStore.subscribe(updateCookie);

    return unsubscribe;
  }, [breakpoint, mapVisibilityStore, setIsMapToggledOnCookie]);
};
